import { useEffect, useState } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface UserRole {
  role: string;
}

export interface UserSubscription {
  plan: 'basic' | 'standard';
  status: string;
}

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [isPtaAdmin, setIsPtaAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    let isMounted = true;

    // Fetch user data - returns data for state updates
    const fetchUserData = async (userId: string): Promise<{
      roles: string[];
      subscription: UserSubscription | null;
      isPtaAdmin: boolean;
    }> => {
      try {
        // Fetch roles
        const { data: rolesData, error: rolesError } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', userId);

        if (rolesError) throw rolesError;
        const fetchedRoles = rolesData?.map(r => r.role) || [];

        // Fetch user's PTA
        const { data: memberData, error: memberError } = await supabase
          .from('members')
          .select('pta_id, is_pta_admin')
          .eq('user_id', userId)
          .maybeSingle();

        if (memberError) throw memberError;
        const fetchedIsPtaAdmin = memberData?.is_pta_admin || false;

        // Fetch PTA's subscription
        let fetchedSubscription: UserSubscription | null = null;
        if (memberData?.pta_id) {
          // First try to get active/trial subscription
          let { data: subData, error: subError } = await supabase
            .from('subscriptions')
            .select('plan, status, expires_at')
            .eq('pta_id', memberData.pta_id)
            .in('status', ['active', 'trial'])
            .maybeSingle();

          if (subError) throw subError;

          // If no active subscription, check for trial_expired
          if (!subData) {
            const { data: expiredSub } = await supabase
              .from('subscriptions')
              .select('plan, status, expires_at')
              .eq('pta_id', memberData.pta_id)
              .eq('status', 'trial_expired')
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle();
            
            if (expiredSub) {
              subData = expiredSub;
            }
          }

          // Check if subscription has expired
          if (subData?.expires_at && new Date(subData.expires_at) < new Date()) {
            // Mark as expired
            await supabase
              .from('subscriptions')
              .update({ status: 'expired' })
              .eq('pta_id', memberData.pta_id)
              .eq('status', 'active');
            
            fetchedSubscription = null;
          } else {
            fetchedSubscription = subData;
          }
        }

        return {
          roles: fetchedRoles,
          subscription: fetchedSubscription,
          isPtaAdmin: fetchedIsPtaAdmin
        };
      } catch (error: any) {
        console.error('Error fetching user data:', error);
        toast({
          title: "Error",
          description: "Failed to load user data",
          variant: "destructive",
        });
        return { roles: [], subscription: null, isPtaAdmin: false };
      }
    };

    // Apply fetched data to state (with mount check)
    const applyUserData = (data: { roles: string[]; subscription: UserSubscription | null; isPtaAdmin: boolean }) => {
      if (!isMounted) return;
      setRoles(data.roles);
      setSubscription(data.subscription);
      setIsPtaAdmin(data.isPtaAdmin);
    };

    // Clear user data from state
    const clearUserData = () => {
      if (!isMounted) return;
      setRoles([]);
      setSubscription(null);
      setIsPtaAdmin(false);
    };

    // LISTENER FIRST - Set up auth state change listener BEFORE getSession
    // This ensures no auth events are missed during initialization
    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        console.log('[useAuth] onAuthStateChange:', event, !!newSession?.user);
        
        if (!isMounted) return;
        
        setSession(newSession);
        setUser(newSession?.user ?? null);

        // Fire and forget - don't await, don't control loading state
        // The initial load handles loading state
        if (newSession?.user) {
          fetchUserData(newSession.user.id).then(applyUserData);
        } else {
          clearUserData();
        }
      }
    );

    // INITIAL LOAD - controls isLoading
    // This runs after the listener is set up
    const initializeAuth = async () => {
      console.log('[useAuth] Initializing auth...');
      try {
        const { data: { session: initialSession } } = await supabase.auth.getSession();
        
        if (!isMounted) return;
        
        console.log('[useAuth] Initial session:', !!initialSession?.user);
        
        setSession(initialSession);
        setUser(initialSession?.user ?? null);

        // Fetch data BEFORE setting loading false
        if (initialSession?.user) {
          const userData = await fetchUserData(initialSession.user.id);
          if (isMounted) {
            applyUserData(userData);
          }
        }
      } catch (error) {
        console.error('[useAuth] Error during initialization:', error);
      } finally {
        // Only set loading false after ALL data is fetched
        if (isMounted) {
          console.log('[useAuth] Initialization complete, setting loading=false');
          setLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
      authSubscription.unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
    return { error };
  };

  const signUp = async (email: string, password: string, fullName?: string) => {
    const redirectUrl = `${window.location.origin}/pta-admin`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
        }
      }
    });
    
    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
    return { data, error };
  };

  const signOut = async () => {
    try {
      // Use local scope to avoid "session not found" errors with stale sessions
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      if (error) {
        // Log warning but don't show error - session might already be gone
        console.warn("Sign out warning:", error.message);
      }
    } catch (e) {
      console.warn("Sign out exception:", e);
    }
    
    // Always clear local state regardless of API response
    setUser(null);
    setSession(null);
    setRoles([]);
    setSubscription(null);
    setIsPtaAdmin(false);
    
    // Navigate to auth page using window.location for clean redirect
    window.location.href = '/auth';
  };

  const hasRole = (role: string) => roles.includes(role);
  const hasPlan = (plan: 'basic' | 'standard') => subscription?.plan === plan || (plan === 'basic' && subscription?.plan === 'standard');

  return {
    user,
    session,
    roles,
    subscription,
    isPtaAdmin,
    loading,
    signIn,
    signUp,
    signOut,
    hasRole,
    hasPlan,
  };
};
